import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Plus, Pencil, Trash2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

const ICON_OPTIONS = [
  { value: "fa-brands fa-linkedin", label: "LinkedIn" },
  { value: "fa-brands fa-github", label: "GitHub" },
  { value: "fa-brands fa-twitter", label: "Twitter" },
  { value: "fa-brands fa-instagram", label: "Instagram" },
  { value: "fa-brands fa-facebook", label: "Facebook" },
  { value: "fa-brands fa-youtube", label: "YouTube" },
  { value: "fa-brands fa-dribbble", label: "Dribbble" },
  { value: "fa-brands fa-behance", label: "Behance" },
  { value: "fa-solid fa-envelope", label: "Email" },
  { value: "fa-solid fa-link", label: "Link" },
  { value: "fa-solid fa-code", label: "Code" },
  { value: "fa-solid fa-laptop-code", label: "Laptop Code" },
  { value: "fa-solid fa-database", label: "Database" },
  { value: "fa-solid fa-server", label: "Server" },
  { value: "fa-brands fa-react", label: "React" },
  { value: "fa-brands fa-node-js", label: "Node.js" },
  { value: "fa-brands fa-python", label: "Python" },
  { value: "fa-brands fa-js", label: "JavaScript" },
  { value: "fa-brands fa-html5", label: "HTML5" },
  { value: "fa-brands fa-css3-alt", label: "CSS3" },
  { value: "fa-solid fa-pen-nib", label: "Design" },
  { value: "fa-solid fa-camera", label: "Photography" },
  { value: "fa-solid fa-gamepad", label: "Gaming" },
  { value: "fa-solid fa-music", label: "Music" },
  { value: "fa-solid fa-plane", label: "Travel" },
  { value: "fa-solid fa-book", label: "Reading" },
  { value: "fa-solid fa-dumbbell", label: "Fitness" },
  { value: "fa-solid fa-heart", label: "Heart" },
  { value: "fa-solid fa-star", label: "Star" },
  { value: "fa-solid fa-briefcase", label: "Briefcase" },
  { value: "fa-solid fa-chart-line", label: "Chart Line" },
  { value: "fa-solid fa-chart-pie", label: "Chart Pie" },
  { value: "fa-solid fa-users", label: "Users" },
  { value: "fa-solid fa-user-tie", label: "User Tie" },
  { value: "fa-solid fa-headset", label: "Support" },
  { value: "fa-solid fa-sack-dollar", label: "Money" },
  { value: "fa-solid fa-handshake", label: "Handshake" },
  { value: "fa-solid fa-bullhorn", label: "Marketing" },
  { value: "fa-solid fa-person-running", label: "Running" },
  { value: "fa-solid fa-person-swimming", label: "Swimming" },
  { value: "fa-solid fa-person-biking", label: "Biking" },
  { value: "fa-solid fa-mountain", label: "Hiking/Outdoors" },
  { value: "fa-solid fa-campground", label: "Camping" },
  { value: "fa-solid fa-futbol", label: "Soccer" },
  { value: "fa-solid fa-basketball", label: "Basketball" },
  { value: "fa-solid fa-medal", label: "Medal" },
  { value: "fa-solid fa-microphone", label: "Public Speaking" },
  { value: "fa-solid fa-chalkboard-user", label: "Teaching" },
  { value: "fa-solid fa-people-group", label: "Audience" },
  { value: "fa-solid fa-comments", label: "Discussion" },
  { value: "fa-solid fa-podcast", label: "Podcast" },
  { value: "fa-solid fa-pen-fancy", label: "Writing" }
];

const blank = { title: "", description: "", iconUrl: "Sparkles", displayOrder: 1 };

export default function Expertise() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchExpertise = async () => {
    try {
      setLoading(true);
      const res = await api.get("/Expertise");
      if (res.success) {
        setItems(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch expertise");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpertise();
  }, []);

  const save = async () => {
    if (!form.title) {
      toast.error("Title is required.");
      return;
    }
    try {
      if (editing) {
        const res = await api.put(`/Expertise/${editing.id}`, form);
        if (res.success) {
          toast.success("Expertise updated.");
          fetchExpertise();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const res = await api.post("/Expertise", form);
        if (res.success) {
          toast.success("Expertise added.");
          fetchExpertise();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/Expertise/${toDelete.id}`);
      if (res.success) {
        toast.success("Expertise removed.");
        fetchExpertise();
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Expertise"
        description="The areas where you bring deep, opinionated craft."
        testId="expertise"
        action={() => { setEditing(null); setForm(blank); setModalOpen(true); }}
        actionLabel="Add Expertise"
        actionIcon={Plus}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((e) => {
          const isUrl = e.iconUrl && (e.iconUrl.startsWith("http") || e.iconUrl.startsWith("/"));
          const isFA = e.iconUrl && (e.iconUrl.includes("fa-") || e.iconUrl.includes("fa "));
          const Icon = (!isUrl && !isFA) ? (Icons[e.iconUrl] || Icons.Sparkles) : null;
          return (
            <Card
              key={e.id}
              data-testid={`expertise-card-${e.id}`}
              className="group relative p-6 hover:border-foreground/20 transition-all duration-200 hover:-translate-y-0.5 cursor-default overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/5 group-hover:bg-primary/10 transition-colors" />
              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-foreground text-background flex items-center justify-center mb-5 group-hover:bg-primary transition-colors overflow-hidden">
                  {isUrl ? (
                    <img src={e.iconUrl} alt="icon" className="h-6 w-6 object-contain" />
                  ) : isFA ? (
                    <i className={`${e.iconUrl} text-xl`}></i>
                  ) : (
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-lg font-semibold tracking-tight">{e.title}</h3>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-foreground group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} />
                </div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{e.description}</p>
                <div className="flex items-center gap-1 mt-5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditing(e); setForm(e); setModalOpen(true); }} data-testid={`expertise-edit-${e.id}`}>
                    <Pencil className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    onClick={() => { setToDelete(e); setConfirmOpen(true); }}
                    data-testid={`expertise-delete-${e.id}`}
                  >
                    <Trash2 className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="expertise-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit expertise" : "Add expertise"}</DialogTitle>
            <DialogDescription>Define an area of deep, opinionated craft.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title</Label>
              <Input data-testid="expertise-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="Design Systems" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Icon</Label>
              <Select value={form.iconUrl} onValueChange={(v) => setForm({ ...form, iconUrl: v })}>
                <SelectTrigger className="mt-1.5" data-testid="expertise-icon">
                  <SelectValue placeholder="Select an icon" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {ICON_OPTIONS.map((icon) => (
                    <SelectItem key={icon.value} value={icon.value}>
                      <div className="flex items-center gap-2">
                        <i className={`${icon.value} w-4 text-center text-sm`}></i>
                        <span>{icon.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea data-testid="expertise-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="expertise-save">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete expertise?"
        description="This will remove the expertise from your portfolio."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}
